<script setup lang="ts">
  import { useLegacyAppStore } from '@/store/legacy-app.store'
  import { Modal, UiButton } from '@logistiek-zonder-papier/lzp-library'

  const appStore = useLegacyAppStore()

  function reload() {
    window.location.reload()
  }
</script>

<template>
  <Modal class="error-modal" v-model:show="appStore.showErrorModal" size="small">
    <template #default>
      <div class="error-body">
        <div class="error-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h2 class="error-title">Er is iets fout gegaan!</h2>
        <p class="error-text">
          Er is een onverwachte fout opgetreden. Wilt u doorgaan of de pagina opnieuw laden?
        </p>
        <p class="error-note">
          Let op: als u de pagina opnieuw laadt, kan uw huidige werk verloren gaan.
        </p>
      </div>
    </template>
    <template #footer="{ close }">
      <div class="error-footer">
        <UiButton class="error-footer-button" label="Sluiten" @click="close" />
        <UiButton
          class="error-footer-button"
          label="Pagina herladen"
          variant="prominent"
          icon="refresh"
          @click="reload"
        />
      </div>
    </template>
  </Modal>
  <div class="wrapper">
    <RouterView />
  </div>
</template>

<style scoped>
  .modal-overlay.error-modal {
    z-index: 30;
  }

  .error-modal :deep(.modal-container) {
    position: relative;
    width: min(30rem, calc(100vw - 2rem));
    min-width: 0;
    border-radius: 1rem;
    box-shadow:
      0 1.5rem 3rem -0.75rem rgb(0 0 0 / 25%),
      0 0 0 1px rgb(0 0 0 / 4%);
    overflow: hidden;
  }

  .error-modal :deep(.modal-header) {
    position: absolute;
    top: var(--spacing-s, 0.5rem);
    right: var(--spacing-s, 0.5rem);
    height: auto;
    padding: 0;
    border: none;
  }

  .error-modal :deep(.modal-body) {
    padding: var(--spacing-xl, 2rem) var(--spacing-l, 1.5rem) var(--spacing-m, 1rem);
  }

  .error-modal :deep(.modal-footer) {
    border-top: none;
    padding: var(--spacing-m, 1rem) var(--spacing-l, 1.5rem) var(--spacing-l, 1.5rem);
  }

  .error-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--spacing-s, 0.5rem);
  }

  .error-icon {
    display: grid;
    place-items: center;
    width: 4rem;
    height: 4rem;
    margin-bottom: var(--spacing-s, 0.5rem);
    border-radius: 50%;
    color: var(--colours-support-negative, #d64545);
    background: var(--colours-labels-red, #ffe5e5);
    box-shadow: 0 0 0 0.5rem color-mix(in srgb, var(--colours-labels-red, #ffe5e5) 45%, transparent);
  }

  .error-icon svg {
    width: 2rem;
    height: 2rem;
  }

  .error-title {
    margin: 0;
    color: var(--colours-grey-5, #1f1f1f);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .error-text {
    margin: 0;
    max-width: 24rem;
    color: var(--colours-grey-4, #656f85);
    font-size: 1rem;
    line-height: 1.5;
  }

  .error-note {
    margin: var(--spacing-s, 0.5rem) 0 0;
    padding: var(--spacing-s, 0.5rem) var(--spacing-m, 1rem);
    width: 100%;
    border-radius: 0.5rem;
    background: var(--colours-grey-2, #f7f9fa);
    color: var(--colours-grey-4, #656f85);
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .error-footer {
    display: flex;
    gap: var(--spacing-s, 0.5rem);
    width: 100%;
  }

  .error-footer-button {
    flex: 1;
    justify-content: center;
  }

  @media (max-width: 30rem) {
    .error-footer {
      flex-direction: column-reverse;
    }
  }
</style>
